import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for managing page metadata
 * 
 * @param {string} title - Page title (will be appended to site name)
 * @param {string} description - Meta description
 * @param {string} image - Open Graph image URL (defaults to site default)
 * @param {string} url - Canonical URL (defaults to current page)
 * @param {string} type - Open Graph type (default: 'website')
 * @param {object} keywords - Additional keywords for meta tags
 * @param {object} structuredData - JSON-LD structured data object
 */
export default function SEO({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website',
  keywords = {},
  structuredData = null,
  useFullTitle = false // If true, use title as-is without appending site name
}) {
  const siteName = 'BodAI';
  const siteUrl = 'https://bodai.io';
  const defaultImage = `${siteUrl}/og-image.jpg`;
  const defaultDescription = 'Master the art of presence with BodAI. Our advanced AI analyzes your body language, voice, and presence in real-time to help you become a more confident and influential communicator.';
  
  // Build full title
  // If useFullTitle is true, use title as-is (for dynamic journey-based titles)
  // Otherwise, append site name in standard format
  const defaultTitle = `${siteName} - Personal AI Body Language & Communication Coach`;
  const fullTitle = useFullTitle 
    ? (title || defaultTitle)
    : (title ? `${title} | ${siteName}` : defaultTitle);
  
  // Use provided image or default
  const ogImage = image || defaultImage;
  
  // Build canonical URL
  const canonicalUrl = url || (typeof window !== 'undefined' ? `${siteUrl}${window.location.pathname}` : siteUrl);
  
  // Build keywords string from object
  const keywordsArray = [];
  if (keywords.students) keywordsArray.push(...keywords.students);
  if (keywords.lecturers) keywordsArray.push(...keywords.lecturers);
  if (keywords.managers) keywordsArray.push(...keywords.managers);
  if (keywords.creators) keywordsArray.push(...keywords.creators);
  if (keywords.general) keywordsArray.push(...keywords.general);
  const keywordsString = keywordsArray.length > 0 ? keywordsArray.join(', ') : 'body language AI, communication coach, public speaking AI, AI presence training, BodAI, soft skills AI';
  
  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywordsString} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph Tags (for Facebook, LinkedIn, WhatsApp) */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title ? `${title} | ${siteName}` : `${siteName} | Master Your Presence with AI`} />
      <meta property="og:description" content={description || (title ? defaultDescription : 'AI-powered analysis of your communication skills. Get professional feedback in minutes.')} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title ? `${title} | ${siteName}` : `${siteName} | Master Your Presence with AI`} />
      <meta name="twitter:description" content={description || (title ? defaultDescription : 'AI-powered analysis of your communication skills. Get professional feedback in minutes.')} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@bodai_io" />
      <meta name="twitter:creator" content="@bodai_io" />
      
      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content={siteName} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* JSON-LD Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Generate JSON-LD structured data for SoftwareApplication
 * 
 * @param {object} options - Configuration options
 * @param {string} options.name - Application name (default: 'BodAI')
 * @param {string} options.description - Application description
 * @param {string} options.applicationCategory - Category (default: 'BusinessApplication')
 * @param {string} options.operatingSystem - OS (default: 'Web')
 * @param {string} options.offers - Pricing info
 * @param {string} options.aggregateRating - Rating object
 * @returns {object} JSON-LD object
 */
export function generateSoftwareApplicationSchema({
  name = 'BodAI',
  description = 'AI Body Language Coach - Improve your communication skills with AI-powered body language analysis.',
  applicationCategory = 'BusinessApplication',
  operatingSystem = 'Web',
  offers = {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD'
  },
  aggregateRating = null
} = {}) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: name,
    description: description,
    applicationCategory: applicationCategory,
    operatingSystem: operatingSystem,
    offers: offers,
    url: 'https://bodai.io',
    screenshot: 'https://bodai.io/images/og-default.png',
    featureList: [
      'AI-powered body language analysis',
      'Job interview preparation',
      'Public speaking coaching',
      'Presentation skills training',
      'Real-time video analysis',
      'Personalized feedback'
    ],
    keywords: 'AI body language coach, body language analysis, communication skills, job interview preparation, public speaking, presentation skills'
  };
  
  if (aggregateRating) {
    schema.aggregateRating = aggregateRating;
  }
  
  return schema;
}

/**
 * Pre-configured SEO props for different personas/routes
 */
export const SEOConfigs = {
  // Homepage - General
  homepage: {
    title: 'AI Body Language Coach',
    description: 'Improve your communication skills with AI-powered body language analysis. Perfect for job interviews, public speaking, presentations, and professional development.',
    keywords: {
      general: ['AI body language coach', 'body language analysis', 'communication skills', 'professional development']
    },
    image: 'https://bodai.io/images/og-homepage.png'
  },
  
  // Students - Job Interview Focus
  students: {
    title: 'Job Interview AI Coach',
    description: 'Ace your job interviews with AI-powered body language analysis. Get personalized feedback on your posture, eye contact, gestures, and overall presence to make a great first impression.',
    keywords: {
      students: ['job interview AI', 'interview preparation', 'job interview body language', 'interview coaching', 'career preparation', 'interview skills']
    },
    image: 'https://bodai.io/images/og-students.png'
  },
  
  // Lecturers - Public Speaking Focus
  lecturers: {
    title: 'Public Speaking AI Coach',
    description: 'Enhance your public speaking skills with AI body language analysis. Perfect for lecturers, teachers, and educators who want to improve their classroom presence and presentation delivery.',
    keywords: {
      lecturers: ['public speaking AI', 'lecturer coaching', 'teaching skills', 'presentation skills for teachers', 'classroom presence', 'educational speaking']
    },
    image: 'https://bodai.io/images/og-lecturers.png'
  },
  
  // Managers - Leadership & Presentations
  managers: {
    title: 'Leadership Communication AI Coach',
    description: 'Elevate your leadership presence with AI-powered body language analysis. Perfect for managers and executives who want to improve their communication, presentations, and team interactions.',
    keywords: {
      managers: ['leadership communication', 'executive coaching', 'management skills', 'business presentation skills', 'leadership presence', 'corporate communication']
    },
    image: 'https://bodai.io/images/og-managers.png'
  },
  
  // Creators - Content & Presence
  creators: {
    title: 'UGC Creator Body Language Coach',
    description: 'Improve your on-camera presence and audience engagement with AI body language analysis. Perfect for UGC creators, YouTubers, and influencers who want to connect better with their audience.',
    keywords: {
      creators: ['UGC creator coaching', 'on-camera presence', 'YouTube body language', 'influencer communication', 'video presence', 'audience engagement', 'UGC content creation']
    },
    image: 'https://bodai.io/images/og-creators.png'
  },
  
  // Dashboard
  dashboard: {
    title: 'Dashboard',
    description: 'View your body language analysis results, track your progress, and access personalized coaching recommendations.',
    keywords: {
      general: ['body language dashboard', 'analysis results', 'progress tracking']
    }
  },
  
  // Practice Page
  practice: {
    title: 'Practice Sessions',
    description: 'Practice your communication skills with guided exercises and receive AI-powered feedback on your body language.',
    keywords: {
      general: ['body language practice', 'communication exercises', 'skills practice']
    }
  },
  
  // Pricing Page
  pricing: {
    title: 'Pricing',
    description: 'Choose the perfect plan for your body language coaching needs. Free and premium plans available.',
    keywords: {
      general: ['body language coaching pricing', 'AI coaching plans', 'subscription plans']
    }
  }
};

